// Per-game dashboard views: deploys (deploy API), community (community API), playtime (accounts).

import { api, ApiError, isMissingEndpoint } from "../api.ts";
import type { Game } from "../catalogue.ts";
import { clear, errorBox, fmtDate, fmtPlaytime, h, section, toast } from "../dom.ts";
import { nameSpan, summaryLine, type Review, type ReviewSummary } from "./game.ts";
import { hiddenTargets, parseLog, parseQueue, type LogEntry, type Queue } from "../moderation.ts";
import { hiddenList, hiddenTitles, modAction, queueRow } from "../mod-queue.ts";
import { selling } from "../selling.ts";

// ---- deploys -----------------------------------------------------------------------------------------------

interface Version {
  version: string;
  n: number;
  status: "uploading" | "ready" | string;
  url: string;
  files: number;
  bytes: number;
  threads: boolean;
  created_at: string;
  finalized_at: string | null;
}

interface DeployGame {
  slug: string;
  owner: string;
  live: string | null;
  url: string;
  review: { status: "approved" | "rejected" | "pending" | string; note?: string; at?: string } | null;
  listed: boolean;
  versions: Version[];
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const reviewBadge = (s: string | undefined) =>
  h("span", { class: `badge ${s === "approved" ? "ok" : s === "rejected" ? "bad" : "warn"}` }, s === "approved" ? "Approved" : s === "rejected" ? "Rejected" : "Waiting for review");

/** Only links the deploy service hands out (its own build origin on localhost in dev, https in production). */
function safeHref(u: string): string | null {
  try {
    const url = new URL(u);
    return url.protocol === "https:" || (url.protocol === "http:" && url.hostname.endsWith("localhost")) ? url.href : null;
  } catch {
    return null;
  }
}

export function gameDeploy(g: Game): HTMLElement {
  const host = h("div");
  const load = () =>
    clear(
      host,
      section(null, async (body) => {
        let d: DeployGame;
        try {
          d = await api<DeployGame>("deploy", `/games/${g.slug}`);
        } catch (e) {
          if (e instanceof ApiError && (e.code === "unknown_game" || e.status === 404))
            return clear(body, h("div", { class: "panel" }, h("h2", null, "No versions yet"), howToDeploy(g)));
          if (e instanceof ApiError && e.code === "forbidden") return clear(body, h("p", { class: "error" }, "Someone else deploys under this slug. Ask us to check ownership."));
          throw e;
        }
        const live = d.versions.find((v) => v.version === d.live);
        const href = safeHref(d.url);
        clear(
          body,
          h(
            "div",
            { class: "kpis" },
            h("div", { class: "kpi" }, h("div", { class: "k" }, "Live"), h("div", { class: "v" }, live ? `v${live.n}` : "—"), href ? h("a", { class: "small", href, target: "_blank", rel: "noopener" }, "Open live build") : null),
            h("div", { class: "kpi" }, h("div", { class: "k" }, "Review"), h("div", { class: "v" }, reviewBadge(d.review?.status)), d.review?.note ? h("div", { class: "dim small" }, d.review.note) : null),
            h("div", { class: "kpi" }, h("div", { class: "k" }, "In the catalogue"), h("div", { class: "v" }, d.listed ? "Listed" : "Not listed"), h("div", { class: "dim small" }, "Listing needs an approved review and a live version")),
          ),
          h(
            "div",
            { class: "panel" },
            h("h2", null, "Versions"),
            h(
              "table",
              null,
              h("thead", null, h("tr", null, h("th", null, "Version"), h("th", null, "Status"), h("th", { class: "num" }, "Size"), h("th", null, "Uploaded"), h("th", null, h("span", { class: "sr-only" }, "Actions")))),
              h(
                "tbody",
                null,
                [...d.versions]
                  .sort((a, b) => b.n - a.n)
                  .map((v) => {
                    const vh = safeHref(v.url);
                    const isLive = v.version === d.live;
                    return h(
                      "tr",
                      null,
                      h("td", null, vh ? h("a", { href: vh, target: "_blank", rel: "noopener" }, `v${v.n}`) : `v${v.n}`, v.threads ? h("span", { class: "dim small" }, " · threads") : null),
                      h("td", null, isLive ? h("span", { class: "badge ok" }, "Live") : h("span", { class: "badge" }, v.status === "ready" ? "Ready" : v.status)),
                      h("td", { class: "num" }, `${fmtBytes(v.bytes)} · ${v.files} ${v.files === 1 ? "file" : "files"}`),
                      h("td", { class: "small" }, v.created_at.slice(0, 16).replace("T", " ")),
                      h(
                        "td",
                        null,
                        !isLive && v.status === "ready"
                          ? h(
                              "button",
                              {
                                type: "button",
                                class: "btn small ghost",
                                onclick: async (e: Event) => {
                                  (e.target as HTMLButtonElement).disabled = true;
                                  try {
                                    await api("deploy", `/games/${g.slug}/rollback`, { body: { to: v.n } });
                                    toast(`v${v.n} is live`);
                                    load();
                                  } catch (x) {
                                    toast(x instanceof Error ? x.message : String(x));
                                    (e.target as HTMLButtonElement).disabled = false;
                                  }
                                },
                              },
                              d.live && v.n < Number(d.live.split("@")[1]) ? "Roll back to this" : "Make live",
                            )
                          : null,
                      ),
                    );
                  }),
              ),
            ),
          ),
          h("div", { class: "panel" }, h("h2", null, "Ship a new version"), howToDeploy(g)),
        );
      }),
    );
  load();
  return h("div", null, h("h1", null, `${g.title}: Deploys`), host);
}

function howToDeploy(g: Game): HTMLElement {
  return h(
    "div",
    null,
    h("p", null, "Build your game, then upload the folder with the Threedom CLI. Every upload is a new version you can roll back to."),
    h("pre", { class: "note" }, h("code", null, `thrdm deploy ./dist --slug ${g.slug}`)),
    h("p", { class: "dim small" }, "Versions play on their own address right away; they're listed in the catalogue after review."),
  );
}

// ---- community ---------------------------------------------------------------------------------------------

type Tab = "reviews" | "bugs" | "playtest" | "devlog" | "moderation";
const TABS: [Tab, string][] = [
  ["reviews", "Reviews"],
  ["bugs", "Bugs"],
  ["playtest", "Playtest"],
  ["devlog", "Devlog"],
  ["moderation", "Moderation"],
];

export function gameCommunity(g: Game): HTMLElement {
  const host = h("div");
  const tabs = h("div", { class: "tabs-row", role: "tablist", "aria-label": "Community" });
  let current: Tab = (new URLSearchParams(location.search).get("tab") as Tab) || "reviews";
  if (!TABS.some(([t]) => t === current)) current = "reviews";
  const show = (t: Tab) => {
    current = t;
    history.replaceState(null, "", `${location.pathname}?tab=${t}`);
    clear(
      tabs,
      TABS.map(([id, text]) => h("button", { type: "button", role: "tab", class: `pill${id === t ? " on" : ""}`, "aria-selected": id === t ? "true" : "false", onclick: () => show(id) }, text)),
    );
    const view = { reviews: reviewsTab, bugs: bugsTab, playtest: playtestTab, devlog: devlogTab, moderation: moderationTab }[t];
    clear(host, view(g));
  };
  show(current);
  return h("div", null, h("h1", null, `${g.title}: Community`), tabs, host);
}

function reviewsTab(g: Game): HTMLElement {
  return section(null, async (body) => {
    const r = await api<{ summary: ReviewSummary; reviews: Review[] }>("community", `/games/${g.id}/reviews?limit=100`);
    clear(
      body,
      h("p", null, h("strong", null, summaryLine(r.summary)), r.summary.excluded_burst ? h("span", { class: "dim small" }, ` · ${r.summary.excluded_burst} held back from a review wave`) : null),
      h("p", { class: "dim small" }, "Reviews come only from players with enough play time. You can't review your own game; reply in the discussion instead."),
      r.reviews.length
        ? h(
            "table",
            null,
            h("thead", null, h("tr", null, h("th", null, "Player"), h("th", null, "Verdict"), h("th", null, "Review"), h("th", { class: "num" }, "Played"), h("th", null, "Version"))),
            h(
              "tbody",
              null,
              r.reviews.map((rv) =>
                h(
                  "tr",
                  null,
                  h("td", null, nameSpan(rv.user_id)),
                  h("td", null, h("span", { class: `badge ${rv.recommended ? "ok" : "bad"}` }, rv.recommended ? "Recommended" : "Not recommended")),
                  h("td", null, rv.body || h("span", { class: "dim" }, "(no text)")),
                  h("td", { class: "num" }, fmtPlaytime(rv.playtime_seconds)),
                  h("td", null, `v${rv.version}`),
                ),
              ),
            ),
          )
        : h("p", { class: "dim" }, "No reviews yet."),
    );
  });
}

interface Bug {
  id: string;
  title: string;
  body: string;
  version: string | null;
  os: string | null;
  gpu: string | null;
  console_errors: string[];
  status: "open" | "confirmed" | "fixed" | "wontfix" | "duplicate";
  fixed_in: string | null;
  votes: number;
  created_at: number;
}

const BUG_STATUSES = ["open", "confirmed", "fixed", "wontfix"] as const;

function bugsTab(g: Game): HTMLElement {
  const host = h("div");
  let status: (typeof BUG_STATUSES)[number] = "open";
  const filter = h("div", { class: "filter-row", role: "group", "aria-label": "Status" });
  const draw = () => {
    clear(
      filter,
      BUG_STATUSES.map((s) => h("button", { type: "button", class: `pill${s === status ? " on" : ""}`, "aria-pressed": s === status ? "true" : "false", onclick: () => ((status = s), draw()) }, s === "wontfix" ? "Won't fix" : s[0]!.toUpperCase() + s.slice(1))),
    );
    clear(
      host,
      section(null, async (body) => {
        const [{ bugs }, open] = await Promise.all([
          api<{ bugs: Bug[] }>("community", `/games/${g.id}/bugs?status=${status}&sort=votes&limit=100`),
          api<{ bugs: Bug[] }>("community", `/games/${g.id}/bugs?status=open&sort=votes&limit=100`),
        ]);
        const targets = [...open.bugs];
        clear(body, bugs.length ? h("ul", { class: "reviews" }, bugs.map((b) => bugRow(b, targets, draw))) : h("p", { class: "dim" }, "Nothing here."));
      }),
    );
  };
  draw();
  return h("div", null, h("p", { class: "dim small" }, "Players report bugs from the Banger app with the version, system and console errors attached. \"Affected\" counts the reporter, everyone who said \"me too\", and merged duplicates."), filter, host);
}

function bugRow(b: Bug, open: Bug[], redraw: () => void): HTMLElement {
  const msg = h("div", { "aria-live": "polite" });
  const act = async (path: string, body: unknown, done: string) => {
    try {
      const r = await api<{ notified?: number }>("community", path, { body });
      toast(r?.notified ? `${done}; ${r.notified} ${r.notified === 1 ? "player" : "players"} notified` : done);
      redraw();
    } catch (e) {
      clear(msg, errorBox(e));
    }
  };
  const version = h("input", { size: 6, placeholder: "1.4", "aria-label": `Fixed in version, for ${b.title}`, maxlength: 20 });
  const into = h(
    "select",
    { "aria-label": `Merge ${b.title} into` },
    h("option", { value: "" }, "Merge into…"),
    open.filter((o) => o.id !== b.id).map((o) => h("option", { value: o.id }, `${o.title} (${o.votes})`)),
  );
  return h(
    "li",
    { class: "review" },
    h(
      "div",
      { class: "review-head" },
      h("strong", null, b.title),
      h("span", { class: "badge" }, `${b.votes} affected`),
      h("span", { class: `badge ${b.status === "fixed" ? "ok" : b.status === "confirmed" ? "warn" : ""}` }, b.status === "fixed" && b.fixed_in ? `Fixed in v${b.fixed_in}` : b.status),
    ),
    b.body ? h("p", null, b.body) : null,
    h("p", { class: "dim small" }, [b.version ? `v${b.version}` : null, b.os, b.gpu, fmtDate(b.created_at)].filter(Boolean).join(" · ")),
    b.console_errors.length ? h("pre", { class: "note small" }, h("code", null, b.console_errors.slice(0, 5).join("\n"))) : null,
    b.status === "duplicate"
      ? null
      : h(
          "div",
          { class: "actions" },
          b.status !== "confirmed" && b.status !== "fixed" ? h("button", { type: "button", class: "btn small ghost", onclick: () => act(`/bugs/${b.id}/status`, { status: "confirmed" }, "Confirmed") }, "Confirm") : null,
          b.status !== "fixed"
            ? h(
                "form",
                {
                  class: "inline",
                  onsubmit: (e: Event) => {
                    e.preventDefault();
                    const v = version.value.trim();
                    if (!v) return clear(msg, h("p", { class: "error" }, "Which version has the fix?"));
                    void act(`/bugs/${b.id}/status`, { status: "fixed", fixed_in: v }, `Marked fixed in v${v}`);
                  },
                },
                version,
                h("button", { type: "submit", class: "btn small" }, "Mark fixed"),
              )
            : h("button", { type: "button", class: "btn small ghost", onclick: () => act(`/bugs/${b.id}/status`, { status: "open" }, "Reopened") }, "Reopen"),
          b.status !== "wontfix" && b.status !== "fixed" ? h("button", { type: "button", class: "btn small ghost", onclick: () => act(`/bugs/${b.id}/status`, { status: "wontfix" }, "Marked won't fix") }, "Won't fix") : null,
          b.status === "open" || b.status === "confirmed"
            ? h(
                "form",
                {
                  class: "inline",
                  onsubmit: (e: Event) => {
                    e.preventDefault();
                    if (into.value) void act(`/bugs/${b.id}/merge`, { into: into.value }, "Merged");
                  },
                },
                into,
                h("button", { type: "submit", class: "btn small ghost" }, "Merge"),
              )
            : null,
        ),
    msg,
  );
}

interface Question {
  id: string;
  prompt: string;
  active: boolean | number;
  created_at: number;
}

interface Answers {
  count: number;
  median_session_seconds: number | null;
  buckets: { label: string; count: number }[];
  answers: { id: string; user_id: string; session_seconds: number; version: string | null; answer: string; created_at: number }[];
}

export function hbars(rows: { label: string; count: number }[]): HTMLElement {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return h(
    "div",
    { class: "hbars", role: "img", "aria-label": rows.map((r) => `${r.label}: ${r.count}`).join(", ") },
    rows.map((r) => h("div", { class: "hbar" }, h("span", null, r.label), h("span", { class: "track" }, h("span", { class: "fill", style: { width: `${(r.count / max) * 100}%`, display: "block" } })), h("span", { class: "num" }, r.count))),
  );
}

function playtestTab(g: Game): HTMLElement {
  const host = h("div");
  const load = () =>
    clear(
      host,
      section(null, async (body) => {
        const { questions } = await api<{ questions: Question[] }>("community", `/games/${g.id}/playtest/questions?all=1`);
        clear(body, questions.length ? questions.map((q) => questionView(q, load)) : h("p", { class: "dim" }, "No questions yet."));
      }),
    );
  const prompt = h("input", { name: "prompt", placeholder: "What made you stop playing?", maxlength: 500, "aria-label": "Question for players", style: { flex: "1", minWidth: "200px" } });
  const err = h("div");
  const form = h(
    "form",
    {
      class: "actions",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        if (!prompt.value.trim()) return;
        try {
          await api("community", `/games/${g.id}/playtest/questions`, { body: { prompt: prompt.value.trim() } });
          prompt.value = "";
          clear(err);
          toast("Question added; players see it after their next session");
          load();
        } catch (x) {
          clear(err, errorBox(x));
        }
      },
    },
    prompt,
    h("button", { type: "submit", class: "btn small" }, "Ask players"),
  );
  load();
  return h("div", null, h("div", { class: "panel" }, h("h2", null, "Ask a question after a session"), h("p", { class: "dim small" }, "The Banger app asks it when a player stops. Answers come with how long they played, so you can see where people drop off."), form, err), host);
}

function questionView(q: Question, reload: () => void): HTMLElement {
  return h(
    "div",
    { class: "panel" },
    h(
      "div",
      { class: "section-head" },
      h("h2", null, q.prompt),
      h(
        "button",
        {
          type: "button",
          class: "btn small ghost",
          onclick: async () => {
            await api("community", `/playtest/questions/${q.id}`, { method: "PATCH", body: { active: !q.active } }).catch((e) => toast(String(e)));
            reload();
          },
        },
        q.active ? "Stop asking" : "Ask again",
      ),
    ),
    q.active ? null : h("p", { class: "dim small" }, "Not being asked right now."),
    section(null, async (body) => {
      const a = await api<Answers>("community", `/playtest/questions/${q.id}/answers`);
      clear(
        body,
        h("p", { class: "small" }, `${a.count} ${a.count === 1 ? "answer" : "answers"}`, a.median_session_seconds ? ` · median session ${fmtPlaytime(a.median_session_seconds)}` : ""),
        a.count ? hbars(a.buckets) : null,
        a.answers.length
          ? h(
              "table",
              { style: { marginTop: "12px" } },
              h("thead", null, h("tr", null, h("th", { class: "num" }, "Session"), h("th", null, "Answer"), h("th", null, "Version"))),
              h("tbody", null, a.answers.slice(0, 50).map((x) => h("tr", null, h("td", { class: "num" }, fmtPlaytime(x.session_seconds)), h("td", null, x.answer), h("td", null, x.version ? `v${x.version}` : "")))),
            )
          : null,
      );
    }),
  );
}

interface Thread {
  id: string;
  title: string;
  body: string;
  reply_count: number;
  created_at: number;
}

function devlogTab(g: Game): HTMLElement {
  const list = h("div");
  const load = () =>
    clear(
      list,
      section("Posted", async (body) => {
        const { threads } = await api<{ threads: Thread[] }>("community", `/games/${g.id}/threads?kind=devlog&limit=50`);
        clear(
          body,
          threads.length
            ? h("ul", { class: "reviews" }, threads.map((t) => h("li", { class: "review" }, h("div", { class: "review-head" }, h("strong", null, t.title), h("span", { class: "dim small" }, `${fmtDate(t.created_at)} · ${t.reply_count} ${t.reply_count === 1 ? "reply" : "replies"}`)), h("p", null, t.body))))
            : h("p", { class: "dim" }, "No devlogs yet."),
        );
      }),
    );
  const err = h("div", { "aria-live": "polite" });
  const title = h("input", { id: "dl-title", required: true, minlength: 3, maxlength: 140, placeholder: "Patch 1.4: …" });
  const text = h("textarea", { id: "dl-body", rows: 6, placeholder: "What changed, what's next" });
  const form = h(
    "form",
    {
      class: "stack",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        try {
          const r = await api<{ notified: number }>("community", `/games/${g.id}/threads`, { body: { kind: "devlog", title: title.value.trim(), body: text.value } });
          title.value = "";
          text.value = "";
          clear(err);
          toast(`Posted; ${r.notified} ${r.notified === 1 ? "player" : "players"} who hearted ${g.title} will see it`);
          load();
        } catch (x) {
          clear(err, errorBox(x));
        }
      },
    },
    h("div", { class: "field" }, h("label", { for: "dl-title" }, "Title"), title),
    h("div", { class: "field" }, h("label", { for: "dl-body" }, "Post"), text),
    h("button", { type: "submit", class: "btn" }, "Post devlog"),
    err,
  );
  load();
  return h("div", null, h("div", { class: "panel" }, h("h2", null, "New devlog"), h("p", { class: "dim small" }, "Everyone who hearted the game gets it in the Banger app."), form), list);
}

/**
 * Reports on the game's page. Moderators get everything; the game's creator gets the scoped queue (community wave 6)
 * with hide/unhide/dismiss on their own game, dismiss only on reviews, and no remove or bans.
 */
function moderationTab(g: Game): HTMLElement {
  const host = h("div");
  const load = () =>
    clear(
      host,
      section(null, async (body) => {
        let q: Queue;
        try {
          q = parseQueue(await api("community", `/moderation/queue?status=open&game=${encodeURIComponent(g.id)}`));
        } catch (e) {
          if (e instanceof ApiError && e.code === "not_moderator")
            return clear(body, h("p", null, "Reports on this game's page go to the Banger moderators. Only the game's creator and moderators see them here."));
          throw e;
        }
        const log = await api("community", `/moderation/log?game=${encodeURIComponent(g.id)}&limit=200`).then(parseLog, () => [] as LogEntry[]);
        const hidden = hiddenTargets(log, g.id).filter((x) => x.target_type !== "clip"); // clips have their own page
        const titles = await hiddenTitles(hidden, g.id);
        const mod = modAction(load);
        clear(
          body,
          q.scope === "creator"
            ? h(
                "p",
                { class: "dim small" },
                "You can hide things on your game's page and dismiss reports. Reviews stay up unless a moderator hides them, so a game's creator can't bury its reviews. Removing content and banning are for Banger moderators.",
              )
            : null,
          q.items.length ? h("ul", { class: "reviews" }, q.items.map((it) => queueRow(it, q.scope, mod))) : h("p", { class: "dim" }, "Nothing to review."),
          hidden.length ? h("section", { class: "section" }, h("h2", null, "Hidden"), hiddenList(hidden, titles, q.scope, mod)) : null,
        );
      }),
    );
  load();
  return host;
}

// ---- playtime ----------------------------------------------------------------------------------------------

/**
 * Per-game playtime for the game's creator. Accounts has only per-user service reads today; this is the endpoint
 * requested from the platform track (docs/build/web.md, Requests). Until it exists the page shows sample data,
 * clearly marked.
 */
export const PLAYTIME_PATH = (gameId: string, days: number) => `/creators/me/games/${encodeURIComponent(gameId)}/playtime?days=${days}`;

/** Accounts hides days (and totals) with fewer than `minPlayers` players: their numbers come back as null. */
export interface GamePlaytime {
  gameId: string;
  minPlayers?: number;
  days: { date: string; activeSeconds: number | null; players: number | null; sessions: number | null; suppressed?: boolean }[];
  totals: { players: number | null; activeSeconds: number | null; sessions: number | null; medianSessionSeconds: number | null; suppressed?: boolean };
}

const HIDDEN = "hidden";
/** A playtime number, or "hidden" when accounts suppressed it for privacy. */
export const shown = (v: number | null, fmt: (n: number) => string): string => (v === null ? HIDDEN : fmt(v));

/** Deterministic made-up numbers for the layout while the endpoint is missing. */
export function samplePlaytime(gameId: string, days: number, today = new Date()): GamePlaytime {
  let seed = [...gameId].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
  const rnd = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 2 ** 32);
  const out: GamePlaytime["days"] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today.getTime() - i * 86400000);
    const players = Math.round(40 + 30 * Math.sin(i / 3) + rnd() * 25 + (d.getUTCDay() % 6 === 0 ? 30 : 0));
    const sessions = Math.round(players * (1.2 + rnd() * 0.6));
    out.push({ date: d.toISOString().slice(0, 10), players, sessions, activeSeconds: Math.round(sessions * (500 + rnd() * 900)) });
  }
  const sum = (k: "players" | "sessions" | "activeSeconds") => out.reduce((a, x) => a + (x[k] ?? 0), 0);
  return { gameId, days: out, totals: { players: Math.round(sum("players") * 0.45), sessions: sum("sessions"), activeSeconds: sum("activeSeconds"), medianSessionSeconds: 840 } };
}

export function gamePlaytime(g: Game): HTMLElement {
  return h(
    "div",
    null,
    h("h1", null, `${g.title}: Playtime`),
    section(null, async (body) => {
      let data: GamePlaytime;
      let sample = false;
      try {
        data = await api<GamePlaytime>("accounts", PLAYTIME_PATH(g.id, 30));
      } catch (e) {
        if (!isMissingEndpoint(e)) throw e;
        data = samplePlaytime(g.id, 30);
        sample = true;
      }
      const max = Math.max(1, ...data.days.map((d) => d.activeSeconds ?? 0));
      const few = data.minPlayers ?? 5;
      const anyHidden = data.totals.players === null || data.days.some((d) => d.players === null);
      const num = (n: number) => n.toLocaleString("en");
      clear(
        body,
        sample ? h("p", { class: "note", role: "note" }, h("strong", null, "Sample data. "), "Accounts doesn't serve per-game playtime to creators yet, so these numbers are made up to show the layout.") : null,
        h(
          "div",
          { class: `kpis${sample ? " sample" : ""}` },
          h("div", { class: "kpi" }, h("div", { class: "k" }, "Players, 30 days"), h("div", { class: "v" }, shown(data.totals.players, num))),
          h("div", { class: "kpi" }, h("div", { class: "k" }, "Active play"), h("div", { class: "v" }, shown(data.totals.activeSeconds, fmtPlaytime))),
          h("div", { class: "kpi" }, h("div", { class: "k" }, "Sessions"), h("div", { class: "v" }, shown(data.totals.sessions, num))),
          h("div", { class: "kpi" }, h("div", { class: "k" }, "Median session"), h("div", { class: "v" }, data.totals.medianSessionSeconds ? fmtPlaytime(data.totals.medianSessionSeconds) : "—")),
        ),
        h(
          "div",
          { class: `panel${sample ? " sample" : ""}` },
          h("h2", null, "Active play per day"),
          h(
            "div",
            { class: "bars", role: "img", "aria-label": `Active play per day over ${data.days.length} days, peaking at ${fmtPlaytime(max)}` },
            data.days.map((d) =>
              d.activeSeconds === null
                ? h("span", { class: "bar hidden", title: `${d.date}: hidden (fewer than ${few} players)` })
                : h("span", { class: "bar", title: `${d.date}: ${fmtPlaytime(d.activeSeconds)}, ${d.players} players`, style: { height: `${(d.activeSeconds / max) * 100}%` } }),
            ),
          ),
          h("div", { class: "bars-x" }, h("span", null, data.days[0]?.date ?? ""), h("span", null, data.days.at(-1)?.date ?? "")),
        ),
        anyHidden ? h("p", { class: "dim small" }, `Days with fewer than ${few} players are hidden to protect their privacy.`) : null,
        h("p", { class: "dim small" }, `Active play counts only time with the game focused and a player at the controls.${selling() ? " It's also what routes subscription money to your game." : ""}`),
      );
    }),
    section("Where players stop", async (body) => {
      const { questions } = await api<{ questions: Question[] }>("community", `/games/${g.id}/playtest/questions?all=1`);
      if (!questions.length) return clear(body, h("p", { class: "dim" }, "Ask a playtest question (Community → Playtest) to see session lengths and why people stop."));
      const a = await api<Answers>("community", `/playtest/questions/${questions[0]!.id}/answers`);
      clear(body, h("p", { class: "small" }, `From ${a.count} answers to "${questions[0]!.prompt}"`), hbars(a.buckets));
    }),
  );
}
